/**
 * MOB-100 Stage 22 — mobile-side wire shapes for the Me / Inbox tabs.
 *
 * Mirrors the BFF DTOs declared under `vEvents.Mobile.Bff/Models/Me/`
 * and `vEvents.Mobile.Bff/Models/Inbox/`. Every camelCase field name
 * matches the JSON wire — the mobile renderer is a pure presentation
 * layer.
 *
 * Authority order: live schema SQL → BRD §4.12 → Stage 22 prompt text.
 */

// ---------------------------------------------------------------------
// /api/mobile/me/qr-card
// ---------------------------------------------------------------------

/**
 * Payload returned by `GET /api/mobile/me/qr-card`. The token is the
 * cryptographically-random `mobile.AttendeeCheckInQR.Token` that the
 * QR renderer encodes verbatim. The mobile client never parses the
 * token; the organiser's QR scanner endpoint resolves it server-side.
 */
export interface MeQrCardPayload {
  token: string;
  expiresAt?: string | null;
  issuedAt: string;
  displayName: string;
  eventName: string;
}

// ---------------------------------------------------------------------
// /api/mobile/me/preferences
// ---------------------------------------------------------------------

/**
 * Server-stored mobile preferences (REQ-F-MOB-202). Mobile reads on app
 * boot and writes on toggle change so the haptics + notification
 * settings sync across the user's devices.
 */
export interface MePreferencesPayload {
  hapticsEnabled: boolean;
  notificationCategories: Record<string, boolean>;
  language: string | null;
  highContrast: boolean;
  reduceMotion: boolean;
}

/** Partial body sent to `PATCH /api/mobile/me/preferences`. */
export interface MePreferencesPatch {
  hapticsEnabled?: boolean;
  notificationCategories?: Record<string, boolean>;
  language?: string | null;
  highContrast?: boolean;
  reduceMotion?: boolean;
}

/**
 * Canonical notification category keys served by the BFF default map.
 * The mobile settings UI binds against these directly.
 */
export const ME_NOTIFICATION_CATEGORIES = [
  'direct_messages',
  'mentions',
  'session_reminders',
  'booth_messages',
  'announcements',
  'community_replies'
] as const;

export type MeNotificationCategory = (typeof ME_NOTIFICATION_CATEGORIES)[number];

/** Friendly labels for each canonical notification category. */
export const ME_NOTIFICATION_CATEGORY_LABELS: Record<MeNotificationCategory, string> = {
  direct_messages: 'Direct messages',
  mentions: 'Mentions',
  session_reminders: 'Session reminders',
  booth_messages: 'Booth messages',
  announcements: 'Event announcements',
  community_replies: 'Community replies'
};

// ---------------------------------------------------------------------
// /api/mobile/me  (Stage 8 baseline consumed by Stage 22)
// ---------------------------------------------------------------------

export interface MeProfile {
  userId: number;
  registrationId: number;
  fullName: string;
  email?: string | null;
  avatarUrl?: string | null;
  jobTitle?: string | null;
  company?: string | null;
}

export interface MeSavedItemsSummary {
  sessions: number;
  booths: number;
  resources: number;
  posts: number;
  speakers: number;
}

export interface MeLeaderboardRank {
  rank: number;
  score: number;
  totalParticipants: number;
}

export interface MeWalletPassStatus {
  enabled: boolean;
  issued: boolean;
  issuedAt?: string | null;
}

export interface MeCheckInQr {
  token: string;
  issuedAt: string;
  expiresAt?: string | null;
}

export interface MePayload {
  profile: MeProfile;
  savedItems: MeSavedItemsSummary;
  leaderboardRank?: MeLeaderboardRank | null;
  walletPass: MeWalletPassStatus;
  roles: string[];
  checkInQr: MeCheckInQr;
}

// ---------------------------------------------------------------------
// /api/mobile/inbox  (Stage 8 baseline consumed by Stage 22)
// ---------------------------------------------------------------------

export interface InboxSegment<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
}

export interface InboxMessage {
  channelId: string;
  channelType: string;
  otherPartyDisplayName: string;
  otherPartyAvatarUrl?: string | null;
  previewBody?: string | null;
  lastActivityAt: string;
  unreadCount: number;
}

export interface InboxActivity {
  id?: string;
  /** Legacy alias of activityType — kept for older payloads. */
  kind?: string;
  /** 'Mention' | 'Reaction' | 'Reply'. */
  activityType?: string;
  actorDisplayName?: string;
  actorAvatarUrl?: string | null;
  /** 'WallPost' | 'WallComment' | 'ChatMessage'. */
  targetType?: string;
  targetId?: string;
  /** Reaction emoji / label when activityType is Reaction. */
  reactionType?: string | null;
  body?: string | null;
  preview?: string | null;
  /** In-app deep link the row opens on tap. */
  deepLink?: string | null;
  createdAt?: string;
  isRead?: boolean;
}

export interface InboxNotification {
  id?: string;
  title?: string;
  body?: string | null;
  /** Broadcast scope: 'Global' | 'Room' | 'RegSet'. */
  category?: string;
  createdAt?: string;
  isRead?: boolean;
  deepLink?: string | null;
}

export interface InboxPayload {
  messages: InboxSegment<InboxMessage>;
  activity: InboxSegment<InboxActivity>;
  notifications: InboxSegment<InboxNotification>;
}

export type InboxSegmentKey = 'messages' | 'activity' | 'notifications';

// ---------------------------------------------------------------------
// AI Assist drawer (BKD-100 booth-knowledge conversation pattern)
// ---------------------------------------------------------------------

/**
 * BKD-100 conversation envelope. The BFF wraps Assist's response shape
 * (assist.Conversations + assist.Messages) — explicitly NOT the
 * BDA-100 PlatformConversations surface per the Stage 22 prompt.
 */
export interface AssistConversationDto {
  id: string;
  title?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssistMessageDto {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  createdAt: string;
  isStreaming?: boolean;
}
